import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreateVendorUserMutation,
  useUpdateVendorUserMutation,
} from '../../../../../services/vendorsApi';
import { useParams } from 'react-router-dom';
import { Button, Input, Grid, Typhography, Icon, LoadingSpinner } from 'components/core';
import Modal from 'components/core/Modals/Modal/Modal';
import { onPhoneNumberChangeHandler } from '../../../../../helpers/profileHelper';
import { ERROR_MESSAGES } from 'constants/Validation';
import { phoneNumberValidation } from 'hooks/usePhoneNumberValidation';
import { maskedInput } from 'shared/utilities/inputHandlers';
import { MAIN_EMAIL_LOCKED_HINT } from '../mainLogin';

interface CreateUserFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  accountRole: string;
  notificationsEnabled: boolean;
}

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  editUser?: any; // User data for edit mode
  isEditMode?: boolean;
  /** Main login: its email is where checks go, so the email is not editable here. */
  isEmailLocked?: boolean;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  editUser,
  isEditMode = false,
  isEmailLocked = false,
}) => {
  const { id } = useParams();
  const [createUser, { isLoading: createLoading }] = useCreateVendorUserMutation();
  const [updateUser, { isLoading: updateLoading }] = useUpdateVendorUserMutation();

  // Local state to track success/error messages
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Validation schema
  const createUserSchema = yup.object({
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    email: yup.string().email('Invalid email format').required('Email is required'),
    phone: yup
      .string()
      .required(ERROR_MESSAGES.REQUIRED)
      .test('len', ERROR_MESSAGES.PHONE_NUMBER_INVALID, (value) =>
        phoneNumberValidation(value, isEditMode)
      ),
    accountRole: yup.string().required('Role is required'),
  });

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CreateUserFormData>({
    resolver: yupResolver(createUserSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      accountRole: 'admin',
      notificationsEnabled: true,
    },
  });

  // Reset form when modal opens or set edit data
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && editUser) {
        // Set form with existing user data for edit mode
        reset({
          first_name: editUser.user?.firstName || editUser.user?.first_name || '',
          last_name: editUser.user?.lastName || editUser.user?.last_name || '',
          email: editUser.user?.email || '',
          phone: maskedInput(editUser?.user?.phone?.replace('+1', '')) || '',
          accountRole: editUser.role || editUser.accountRole || 'admin',
          notificationsEnabled: editUser.notificationsEnabled ?? true,
        });
      } else {
        // Reset form for create mode
        reset({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          accountRole: 'admin',
          notificationsEnabled: true,
        });
      }
    }
  }, [isOpen, reset, isEditMode, editUser]);

  // Reset local success/error states when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowSuccess(false);
      setShowError(false);
      setErrorMessage(null);
    }
  }, [isOpen]);

  const onSubmit = async (data: CreateUserFormData) => {
    const submitData = {
      userDetail: {
        email: data.email,
        status: 'active',
        phone: data.phone,
        first_name: data.first_name,
        last_name: data.last_name,
      },
      accountId: id || '',
      accountRole: data.accountRole,
      notificationsEnabled: data.notificationsEnabled,
    };

    try {
      if (isEditMode && editUser) {
        // Update existing user
        await updateUser({
          userId: editUser.user?.id || editUser.id,
          userData: submitData,
        }).unwrap();
        setShowSuccess(true);
      } else {
        // Create new user
        await createUser(submitData).unwrap();
        setShowSuccess(true);
      }

      // Close modal on successful operation
      setTimeout(() => {
        onClose();
      }, 1500); // Close after 1.5 seconds to show success message
    } catch (error: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} user:`, error);
      // Extract error message from API response
      const apiErrorMessage =
        error?.data?.message ||
        error?.message ||
        `Failed to ${isEditMode ? 'update' : 'create'} user. Please try again.`;
      setErrorMessage(apiErrorMessage);
      setShowError(true);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      modalAdditionalClasses="!w-[500px] items-center justify-center px-4 py-8 flex-col"
    >
      {(createLoading || updateLoading) && <LoadingSpinner />}
      <Typhography
        component="p"
        type="h2"
        color="text-grey-900 mb-6 uppercase"
        additionalClasses="text-left"
      >
        {isEditMode ? 'EDIT USER' : 'CREATE NEW USER'}
      </Typhography>

      <div className="absolute cursor-pointer top-7 right-4 text-grey-600" onClick={handleClose}>
        <Icon iconColor="inherit" iconType="CloseIcon" />
      </div>

      {showSuccess && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          User {isEditMode ? 'updated' : 'created'} successfully!
        </div>
      )}

      {showError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {errorMessage || `Failed to ${isEditMode ? 'update' : 'create'} user. Please try again.`}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Grid columnCount={2} columnGap={2}>
          <Controller
            control={control}
            name="first_name"
            render={({ field: { value } }) => (
              <Input
                id="first_name"
                labelName="first_name"
                type="text"
                label="First Name"
                {...register('first_name')}
                error={errors?.first_name?.message}
                value={value}
              />
            )}
          />

          <Controller
            control={control}
            name="last_name"
            render={({ field: { value } }) => (
              <Input
                id="last_name"
                labelName="last_name"
                type="text"
                label="Last Name"
                {...register('last_name')}
                error={errors?.last_name?.message}
                value={value}
              />
            )}
          />
        </Grid>

        <Controller
          control={control}
          name="email"
          render={({ field: { value } }) => (
            <Input
              id="email"
              labelName="email"
              type="email"
              label="Email"
              {...register('email')}
              error={errors?.email?.message}
              value={value}
              readOnly={isEmailLocked}
            />
          )}
        />
        {isEmailLocked && (
          <p className="-mt-2 text-xs text-gray-500" data-testid="main-email-locked-hint">
            {MAIN_EMAIL_LOCKED_HINT}
          </p>
        )}

        <Controller
          control={control}
          name="phone"
          render={({ field: { value } }) => (
            <Input
              id="phone"
              labelName="phone"
              type="text"
              label="Phone"
              {...register('phone')}
              error={errors?.phone?.message}
              maxLength={14}
              onChange={(event) => onPhoneNumberChangeHandler(event, setValue, getValues, 'phone')}
              value={value}
            />
          )}
        />

        <div className="relative flex flex-col w-full mb-4 form-input-wrapper">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="notificationsEnabled"
              {...register('notificationsEnabled')}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label
              htmlFor="notificationsEnabled"
              className="text-14s font-normal text-grey-600 cursor-pointer"
            >
              Enable notifications for this user
            </label>
          </div>
          {errors?.notificationsEnabled && (
            <span className="text-red-500 text-xs mt-1">{errors.notificationsEnabled.message}</span>
          )}
        </div>

        <div className="relative flex flex-col w-full mb-4 form-input-wrapper focused">
          <select
            {...register('accountRole')}
            id="accountRole"
            className="form-select outline-none h-[42px] border border-solid border-grey-100 rounded-lg text-black placeholderColor-gray text-14s text-grey-900 px-4 focus:!border-grey-100 focus:!shadow-none"
          >
            <option value="admin">Admin</option>
            <option value="view-only">View Only</option>
          </select>
          <label
            htmlFor="accountRole"
            className="text-14s font-normal text-grey-600 top-3 left-4 absolute"
          >
            Role
          </label>
          {errors?.accountRole && (
            <span className="text-red-500 text-xs mt-1">{errors.accountRole.message}</span>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
          <Button
            type="submit"
            additionalClasses="px-4 py-2"
            variants="default"
            buttonLabel={isEditMode ? 'Update User' : 'Create User'}
          />
          <Button
            type="button"
            onClick={handleClose}
            additionalClasses="px-4 py-2"
            variants="defaultOutlined"
            buttonLabel="Cancel"
          />
        </div>
      </form>
    </Modal>
  );
};

export default CreateUserModal;
